import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsSalesSummaryData } from "src/features/Reports/types/report.types";
import { REPORTS_SALES_SUMMARY } from "src/constants/queries";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import SalesSummaryReportPage from "./SalesSummaryReportPage";
import SalesByChannelReportPage from "./SalesByChannelReportPage";
import SalesByOrderTypeReportPage from "./SalesByOrderTypeReportPage";
import SalesByCategoryReportPage from "./SalesByCategoryReportPage";

const SALES_TABS = [
  { id: "summary", label: "Summary" },
  { id: "byChannel", label: "By Channel" },
  { id: "byOrderType", label: "By Order Type" },
  { id: "byCategory", label: "By Category" },
];

function buildSummaryCsv(rows: ReportsSalesSummaryData["rows"], range: { from: string; to: string }): string {
  const headers = ["Date", "Gross Sales", "Discounts", "Net Sales", "Tax", "Tips", "Orders", "Avg Ticket"];
  const escape = (v: string) => (v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [headers.join(",")];
  if (rows?.length) {
    const sorted = [...rows].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    for (const r of sorted) {
      lines.push(
        [
          r.date ?? "",
          formatCentsToDollarsWithDollarSign(r.grossSalesCents ?? 0),
          formatCentsToDollarsWithDollarSign(r.discountsCents ?? 0),
          formatCentsToDollarsWithDollarSign(r.netSalesCents ?? 0),
          formatCentsToDollarsWithDollarSign(r.taxCents ?? 0),
          formatCentsToDollarsWithDollarSign(r.tipsCents ?? 0),
          String(r.ordersCount ?? ""),
          formatCentsToDollarsWithDollarSign(r.avgTicketCents ?? 0),
        ].map(escape).join(",")
      );
    }
  }
  return lines.join("\n");
}

/** Format ISO date as YYYYMMDD for compact filenames. */
function toDateSlug(iso: string): string {
  return iso.slice(0, 10).replace(/-/g, "");
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SalesReportPage() {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("summary");

  const range = useMemo(() => {
    if (datePreset === "custom" && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    const r = getReportDateRange(datePreset);
    return { from: r.from, to: r.to };
  }, [datePreset, customFrom, customTo]);

  const handleDatePresetChange = (preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
    if (preset === "custom") {
      setCustomFrom(from);
      setCustomTo(to);
    } else {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const input: ReportQueryInput | null = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );

  const { data: summaryData } = useReportQuery<ReportsSalesSummaryData>(
    REPORTS_SALES_SUMMARY,
    "reportsSalesSummary",
    input
  );

  const handleExportCsv = () => {
    const csv = buildSummaryCsv(summaryData?.rows ?? null, range);
    const filename = `sales-summary-${toDateSlug(range.from)}-${toDateSlug(range.to)}.csv`;
    downloadCsv(csv, filename);
  };

  const exportButton =
    activeTab === "summary" ? (
      <button
        type="button"
        onClick={handleExportCsv}
        className="text-sm font-medium text-[#374151] hover:text-[#111827] border border-[#d1d5db] hover:border-[#9ca3af] rounded-lg px-3 py-2 transition-colors"
      >
        Export CSV
      </button>
    ) : null;

  const tabContent = () => {
    switch (activeTab) {
      case "summary":
        return <SalesSummaryReportPage embedded input={input} />;
      case "byChannel":
        return <SalesByChannelReportPage embedded input={input} />;
      case "byOrderType":
        return <SalesByOrderTypeReportPage embedded input={input} />;
      case "byCategory":
        return <SalesByCategoryReportPage embedded input={input} />;
      default:
        return <SalesSummaryReportPage embedded input={input} />;
    }
  };

  const toolbar = (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-end gap-3">
        <ReportDateRangePicker
          value={datePreset}
          onChange={handleDatePresetChange}
          customFrom={datePreset === "custom" ? customFrom : undefined}
          customTo={datePreset === "custom" ? customTo : undefined}
        />
        {exportButton}
      </div>
      <ReportTabs
        tabs={SALES_TABS}
        activeTabId={activeTab}
        onSelect={setActiveTab}
        inline
        inlineFullWidth
      />
    </div>
  );

  return (
    <ReportPageLayout hideTitle toolbar={toolbar}>
      {tabContent()}
    </ReportPageLayout>
  );
}
